import { useState } from 'react'
import { RefreshIcon, MinusCircleIcon } from '@heroicons/react/outline'
import { differenceInDays, parseISO } from 'date-fns'

import { Order } from 'models'
import { ReadResult } from 'interfaces/queryOptions'
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog'
import { classNames } from 'utils'
import { useViewClient } from 'modules/Clients/hooks/useViewClient'
import { toast } from 'react-toastify'

interface FinalCycleToggleProps {
  order: ReadResult<Order>
}

function isEligible(order: ReadResult<Order>): boolean {
  const plan = order.relationships?.plan?.attributes
  return (
    order.attributes.current_status === 'delivered' &&
    !!order.attributes.end_at &&
    !!order.attributes.activated_at &&
    plan?.renewable === true &&
    plan?.recurring === true &&
    plan?.nature === 'service' &&
    plan?.interval != null &&
    plan?.duration != null
  )
}

export function FinalCycleToggle({ order }: FinalCycleToggleProps) {
  const { setOrderFinalCycle } = useViewClient()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  if (!isEligible(order)) return null

  // final_cycle = true  → ciclo final → NÃO renova
  // final_cycle = false → não é ciclo final → renova
  const isRenewable = order.attributes.final_cycle !== true
  const endAt = order.attributes.end_at as Date | string
  const daysUntilEnd = differenceInDays(
    typeof endAt === 'string' ? parseISO(endAt) : endAt,
    new Date()
  )
  const withinWindow = daysUntilEnd <= 30
  const nextIsRenewable = !isRenewable
  const finalCycleToSend = !nextIsRenewable
  const disabled = withinWindow && isRenewable

  const title = nextIsRenewable
    ? 'Habilitar renovação'
    : 'Desabilitar renovação'

  const message = nextIsRenewable
    ? withinWindow
      ? 'Este pedido está dentro da janela de 30 dias para o encerramento. Ao confirmar, a renovação será executada imediatamente, estendendo a vigência do pedido conforme o plano.'
      : 'O pedido continuará sendo renovado ao final do ciclo atual.'
    : 'O pedido não será renovado ao final do ciclo atual.'

  async function handleConfirm() {
    setConfirmOpen(false)
    setLoading(true)
    try {
      await setOrderFinalCycle(order.id, finalCycleToSend)
      toast.success('Renovação do pedido atualizada com sucesso')
    } catch (error: any) {
      toast.error(
        error.suggestedMessage ?? 'Erro ao atualizar renovação do pedido'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && !loading && setConfirmOpen(true)}
        disabled={disabled || loading}
        className={classNames(
          isRenewable ? 'text-emerald-700' : 'text-gray-400',
          disabled || loading ? 'opacity-50 cursor-not-allowed' : '',
          'relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 focus:z-10 focus:outline-none focus:border-emerald-500 rounded-md'
        )}
      >
        {isRenewable ? (
          <RefreshIcon className="h-5 w-5" aria-hidden="true" />
        ) : (
          <MinusCircleIcon className="h-5 w-5" aria-hidden="true" />
        )}
      </button>

      <ConfirmationDialog
        open={confirmOpen}
        title={title}
        message={message}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
        confirmButtonLabel="Confirmar"
      />
    </>
  )
}
